import { useState, useCallback } from 'react'
import { log } from '@/lib/logger'
import { manageKeyVal } from '@/services/DataService'

// Processing status enum
export enum ProcessingStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  WAITING_FOR_APPROVAL = "waiting_for_approval",
  FAILED = 'failed',
  DRAFT_GENERATED = 'draft_generated',
}

// Type definitions for key-value operations
export interface KeyValEntryData {
  primary_keywords: string[]
  secondary_keywords: string[]
  google_doc_url?: string
  workflow_execution_id?: string
  [key: string]: unknown // Allow additional properties
}

export interface KeyValEntry {
  data: KeyValEntryData
  created_at?: string
  updated_at?: string
}

export interface KeyValListResponse {
  key: string
  data: KeyValEntryData
  created_at: string
  updated_at: string
  metadata: Record<string, unknown>
}

/**
 * Hook to get a single entry from a key-value store bucket by its key
 */
export const useKeyValGet = () => {
  const [loading, setLoading] = useState(false)

  const getBucketEntry = useCallback(
    async (
      entity: string,
      workflowInstanceId: string,
      appName: string,
      key: string
    ): Promise<KeyValListResponse | null> => {
      setLoading(true)
      try {
        const response = await manageKeyVal({
          action: 'get',
          key_val: {
            app_name: appName,
            entity,
            workflow_instance_id: workflowInstanceId,
            key,
          },
        })

        if (response?.status === 'found') {
          return {
            key,
            data: response.data,
            created_at: response.created_at,
            updated_at: response.updated_at,
            metadata: {},
          }
        }

        return null
      } catch (error) {
        log('error', 'Failed to get bucket entry', { entity, key, error })
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return { getBucketEntry, loading }
}

/**
 * Hook to list blog posts from a specific key-value store bucket
 */
export const useKeyValList = () => {
  const [loading, setLoading] = useState(false)

  const listBucket = useCallback(
    async (
      entity,
      workflowInstanceId: string,
      appName: string
    ) => {
      setLoading(true)
      try {
        const response = await manageKeyVal({
          action: 'list',
          key_val: {
            app_name: appName,
            entity,
            workflow_instance_id: workflowInstanceId,
          },
        })

        // Extract blog posts from nested response structure
        if (!response?.data || !Array.isArray(response.data)) {
          return []
        }

        return response.data
      } catch (error) {
        log('error', 'Failed to list bucket', { entity, error })
        return []
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return { listBucket, loading }
}

/**
 * Hook to move a blog post from one bucket to another with atomic-like behavior
 * Steps:
 * 1. List entries from source bucket to get the entry with its UUID key
 * 2. Store the entry in destination bucket (removing workflow_execution_id)
 * 3. Delete the entry from source bucket using its UUID key
 * 4. If delete fails, rollback by deleting from destination bucket
 */
export const useKeyValMove = () => {
  const [loading, setLoading] = useState(false)

  const moveKeyVal = useCallback(
    async (
      entryData: KeyValEntry,
      fromEntity: string,
      toEntity: string,
      workflowInstanceId: string,
      appName: string,
      entryKey?: string
    ): Promise<boolean> => {
      if (fromEntity === toEntity) {
        return true // No-op if same status
      }
      setLoading(true)

      let storedInDestination = false
      let destinationKey: string | null = entryKey || null

      try {
        // Step 1: Resolve entry key from input or by matching source entries
        if (destinationKey) {
          const sourceEntryResponse = await manageKeyVal({
            action: 'get',
            key_val: {
              app_name: appName,
              entity: fromEntity,
              workflow_instance_id: workflowInstanceId,
              key: destinationKey,
            },
          })

          if (sourceEntryResponse?.status !== 'found') {
            throw new Error('Could not find source entry in source bucket')
          }
        } else {
          const listResponse = await manageKeyVal({
            action: 'list',
            key_val: {
              app_name: appName,
              entity: fromEntity,
              workflow_instance_id: workflowInstanceId,
            },
          })

          if (!listResponse?.data || !Array.isArray(listResponse.data)) {
            throw new Error('Failed to list entries from source bucket')
          }

          // Find the entry that matches our data (comparing primary keywords)
          const matchingEntry = listResponse.data.find((entry: KeyValListResponse) => {
            const primaryKeywordsMatch =
              JSON.stringify([...(entry.data?.primary_keywords || [])].sort()) ===
              JSON.stringify([...(entryData.data?.primary_keywords || [])].sort())
            const secondaryKeywordsMatch =
              JSON.stringify([...(entry.data?.secondary_keywords || [])].sort()) ===
              JSON.stringify([...(entryData.data?.secondary_keywords || [])].sort())
            return primaryKeywordsMatch && secondaryKeywordsMatch
          })

          if (!matchingEntry || !matchingEntry.key) {
            throw new Error('Could not find matching entry in source bucket')
          }

          destinationKey = matchingEntry.key
        }

        if (!destinationKey) {
          throw new Error('Could not resolve source entry key')
        }

        log('info', 'Resolved entry in source bucket', {
          fromEntity,
          entryKey: destinationKey,
          primaryKeywords: entryData.data?.primary_keywords
        })

        // Step 2: Store in destination bucket (the data is already cleaned by the caller)
        await manageKeyVal({
          action: 'store',
          key_val: {
            app_name: appName,
            entity: toEntity,
            workflow_instance_id: workflowInstanceId,
            key: destinationKey,
            data: entryData.data,
          },
        })

        storedInDestination = true

        log('info', 'Created in destination bucket', {
          toEntity,
          entryKey: destinationKey,
          primaryKeywords: entryData.data?.primary_keywords
        })

        // Step 3: Delete from source bucket using the UUID key
        try {
          await manageKeyVal({
            action: 'delete',
            key_val: {
              app_name: appName,
              entity: fromEntity,
              workflow_instance_id: workflowInstanceId,
              key: destinationKey,
            },
          })

          log('info', 'Deleted from source bucket', {
            entryKey: destinationKey,
            primaryKeywords: entryData.data?.primary_keywords
          })

          return true
        } catch (deleteError: unknown) {
          // Step 4: Rollback - delete from destination bucket if delete from source fails
          log('error', 'Failed to delete from source bucket, attempting rollback', {
            fromEntity,
            toEntity,
            entryKey: destinationKey,
            error: deleteError,
          })

          try {
            await manageKeyVal({
              action: 'delete',
              key_val: {
                app_name: appName,
                entity: toEntity,
                workflow_instance_id: workflowInstanceId,
                key: destinationKey,
              },
            })

            log('info', 'Rollback successful: deleted from destination bucket', {
              toEntity,
              entryKey: destinationKey,
            })
          } catch (rollbackError: unknown) {
            log('error', 'Rollback failed: entry may exist in both buckets', {
              fromEntity,
              toEntity,
              entryKey: destinationKey,
              rollbackError,
            })
          }

          throw deleteError
        }
      } catch (error) {
        log('error', 'Failed to move entry between buckets', {
          fromEntity,
          toEntity,
          primaryKeywords: entryData.data?.primary_keywords,
          storedInDestination,
          destinationKey,
          error,
        })

        return false
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return { moveKeyVal, loading }
}
